import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * Hook to handle automatic logout after a period of inactivity.
 *
 * CLERK SESSION CONTEXT:
 * - Clerk session tokens expire after 60 seconds but auto-refresh when active
 * - Clerk's "inactivity timeout" is a PREMIUM feature (not available on free tier)
 * - Clerk's "maximum lifetime" is 7 days by default
 * - Problem: Clerk shows isSignedIn=true even when tokens are stale
 *
 * This hook proactively logs users out BEFORE their Clerk session becomes
 * stale, preventing confusing errors during case generation or API calls.
 *
 * @param {Object} options
 * @param {number} options.timeoutMinutes - Minutes of inactivity before logout (default: 120 = 2 hours)
 * @param {number} options.warningMinutes - Minutes before timeout to show warning (default: 5)
 * @param {function} options.onWarning - Callback when warning should be shown
 * @param {function} options.onTimeout - Callback when timeout occurs (before signOut)
 */

// Get timeout from environment or use default (2 hours)
// Note: Clerk's inactivity timeout is a premium feature, so this client-side
// timeout is the primary defense against stale tokens for free-tier Clerk users
const DEFAULT_TIMEOUT_MINUTES = parseInt(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES, 10) || 120;
const DEFAULT_WARNING_MINUTES = 5;

export const useInactivityTimeout = ({
  timeoutMinutes = DEFAULT_TIMEOUT_MINUTES,
  warningMinutes = DEFAULT_WARNING_MINUTES,
  onWarning,
  onTimeout
} = {}) => {
  const { isSignedIn, signOut } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const lastResetRef = useRef(Date.now()); // For throttling activity handler

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;
  const warningAtMs = timeoutMs - warningMs;

  // Reset activity timestamp and clear/restart timers
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);

    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    if (!isSignedIn) return;

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(warningMinutes * 60);

      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      if (onWarning) {
        onWarning();
      }
    }, warningAtMs);

    // Set logout timer
    timeoutRef.current = setTimeout(async () => {
      console.log('Inactivity timeout reached - signing out user');
      setShowWarning(false);

      if (onTimeout) {
        onTimeout();
      }

      try {
        await signOut();
        // Redirect handled by Clerk/ProtectedRoute
      } catch (error) {
        console.error('Error during auto-signout:', error);
        // Force redirect to sign-in on error
        window.location.href = '/sign-in';
      }
    }, timeoutMs);
  }, [isSignedIn, signOut, timeoutMs, warningAtMs, warningMinutes, onWarning, onTimeout]);

  // Dismiss warning and reset activity (user is still active)
  const dismissWarning = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // Set up activity listeners
  useEffect(() => {
    if (!isSignedIn) {
      // Clear timers if not signed in
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowWarning(false);
      return;
    }

    // Activity events to listen for
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttled activity handler (only reset every 30 seconds max)
    const throttleMs = 30000; // 30 seconds

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current >= throttleMs) {
        lastResetRef.current = now;
        resetActivity();
      } else if (showWarning) {
        // If warning is showing, any activity should dismiss it immediately
        resetActivity();
      }
    };

    // Add listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if session should have timed out while tab was hidden
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= timeoutMs) {
          // Session should have expired, sign out now
          console.log('Tab became visible but session expired during inactivity');
          signOut().catch(console.error);
        } else if (elapsed >= warningAtMs) {
          // Show warning immediately
          setShowWarning(true);
          const remaining = Math.ceil((timeoutMs - elapsed) / 1000);
          setRemainingSeconds(remaining);

          // Set new timeout for remaining time
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(async () => {
            await signOut().catch(console.error);
          }, timeoutMs - elapsed);

          // Start countdown
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = setInterval(() => {
            setRemainingSeconds(prev => {
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timers
    resetActivity();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isSignedIn, resetActivity, showWarning, timeoutMs, warningAtMs, signOut]);

  return {
    showWarning,
    remainingSeconds,
    dismissWarning,
    resetActivity
  };
};
